'use client';

/**
 * AuthGateModal — full-screen adaptive security gate for the auth pages.
 *
 * Opens when the browser looks suspicious (repeated failed logins or a
 * server-side `requires_captcha`) and blocks access to the page until the
 * multi-step check is passed. Steps are issued by the server — wrong
 * answers silently produce more steps instead of erroring, so legitimate
 * users simply see a couple of quick challenges while bots keep working.
 */

import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, ShieldCheck, Timer } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import CapacitorModalHeader from '@/components/CapacitorModalHeader';
import { CharacterBlockInput } from '@/components/auth/CharacterBlockInput';
import type { GateStep, GateStatus } from '@/lib/authGate';

const ACCENT = '#f59e0b';

interface AuthGateModalProps {
  open: boolean;
  status: GateStatus;
  step: GateStep | null;
  stepNumber: number;
  error: string | null;
  retryAfter: number;
  onSubmit: (answer: string | number[]) => void;
  onRestart: () => void;
}

function svgToDataUrl(svg: string): string {
  // encodeURIComponent (not btoa) — the select-grid SVGs contain emoji,
  // which are outside btoa's Latin-1 range and would throw.
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// ── Step renderers ──────────────────────────────────────────────

function TextStep({ step, busy, onSubmit }: { step: GateStep; busy: boolean; onSubmit: (a: string) => void }) {
  const [answer, setAnswer] = useState('');
  const complete = answer.length >= 5;

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600 dark:text-gray-300">Type the characters you see in the picture</p>
      <div className="mx-auto w-fit rounded-xl border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800">
        <img
          src={svgToDataUrl(step.image)}
          alt="Security challenge"
          className="h-[80px] w-[250px] object-cover"
          draggable={false}
        />
      </div>
      <CharacterBlockInput value={answer} onChange={setAnswer} disabled={busy} autoFocus />
      <button
        type="button"
        onClick={() => onSubmit(answer)}
        disabled={!complete || busy}
        className="pm-btn-primary w-full disabled:opacity-50"
      >
        {busy ? 'Checking…' : 'Continue'}
      </button>
    </div>
  );
}

function MathStep({ step, busy, onSubmit }: { step: GateStep; busy: boolean; onSubmit: (a: string) => void }) {
  const [answer, setAnswer] = useState('');

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600 dark:text-gray-300">Solve this little sum</p>
      <div className="mx-auto w-fit rounded-xl border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800">
        <img
          src={svgToDataUrl(step.image)}
          alt="Math question"
          className="h-[90px] w-[270px] object-cover"
          draggable={false}
        />
      </div>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={answer}
        onChange={(e) => setAnswer(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && answer.trim() && !busy) onSubmit(answer.trim());
        }}
        disabled={busy}
        autoFocus
        placeholder="Your answer"
        className="pm-input mx-auto block w-32 text-center text-2xl font-bold"
      />
      <button
        type="button"
        onClick={() => onSubmit(answer.trim())}
        disabled={!answer.trim() || busy}
        className="pm-btn-primary w-full disabled:opacity-50"
      >
        {busy ? 'Checking…' : 'Continue'}
      </button>
    </div>
  );
}

function SelectStep({ step, busy, onSubmit }: { step: GateStep; busy: boolean; onSubmit: (a: number[]) => void }) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const gridSize = step.gridSize || 3;

  const toggle = (idx: number) => {
    if (busy) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {step.prompt || 'Tap the matching pictures'}
        {step.targetCount ? ` — there ${step.targetCount === 1 ? 'is' : 'are'} ${step.targetCount}` : ''}
      </p>
      <div className="relative mx-auto w-fit rounded-xl border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800">
        <img
          src={svgToDataUrl(step.image)}
          alt="Picture grid"
          className="block h-[300px] w-[300px]"
          draggable={false}
        />
        {/* Transparent tap zones over the image — the picture contents
            never appear in the DOM as selectable data */}
        <div
          className="absolute inset-2 grid"
          style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)`, gridTemplateRows: `repeat(${gridSize}, 1fr)` }}
        >
          {Array.from({ length: gridSize * gridSize }, (_, idx) => (
            <button
              key={idx}
              type="button"
              aria-label={`Picture ${idx + 1}`}
              disabled={busy}
              onClick={() => toggle(idx)}
              className={`rounded-md transition-all ${
                selected.has(idx)
                  ? 'ring-4 ring-amber-500 ring-inset bg-amber-400/20'
                  : 'hover:bg-black/5 dark:hover:bg-white/10'
              }`}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onSubmit([...selected].sort((a, b) => a - b))}
        disabled={selected.size === 0 || busy}
        className="pm-btn-primary w-full disabled:opacity-50"
      >
        {busy ? 'Checking…' : 'Continue'}
      </button>
    </div>
  );
}

// ── Blocked cooldown panel ──────────────────────────────────────

function BlockedPanel({ retryAfter, onRestart }: { retryAfter: number; onRestart: () => void }) {
  const [cooldown, setCooldown] = useState(retryAfter);

  useEffect(() => {
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const label = cooldown >= 60 ? `${Math.ceil(cooldown / 60)} min` : `${cooldown}s`;

  return (
    <div className="flex flex-col items-center gap-3 py-16">
      <Timer className="h-14 w-14 text-amber-500" />
      <p className="text-lg font-semibold text-gray-900 dark:text-white">Please wait a moment</p>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Too many tries in a row. You can try again in {label}.
      </p>
      <button
        type="button"
        onClick={onRestart}
        disabled={cooldown > 0}
        className="pm-btn-primary mt-3 w-full disabled:opacity-50"
      >
        {cooldown > 0 ? `Try again in ${label}` : 'Try again'}
      </button>
    </div>
  );
}

// ── Modal ───────────────────────────────────────────────────────

export default function AuthGateModal({
  open,
  status,
  step,
  stepNumber,
  error,
  retryAfter,
  onSubmit,
  onRestart,
}: AuthGateModalProps) {
  const [isMobile] = useState(() => typeof window !== 'undefined' && Capacitor.isNativePlatform());

  // Lock body scroll while the gate is up
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9990] flex flex-col bg-white dark:bg-gray-900">
      <CapacitorModalHeader
        icon={ShieldCheck}
        title="Security Check"
        subtitle={
          status === 'step' || status === 'submitting'
            ? <span className="text-[10px] text-gray-400 dark:text-gray-500">Step {stepNumber}</span>
            : undefined
        }
        accentColor={ACCENT}
        borderedIcon
        isMobile={isMobile}
        applyTopSafeArea={isMobile}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-sm px-4 py-8 text-center">

          {(status === 'step' || status === 'submitting') && step && (
            <>
              <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
                Quick check that you&apos;re a real person — it only takes a few seconds.
              </p>
              <div key={step.id}>
                {step.type === 'text' && (
                  <TextStep step={step} busy={status === 'submitting'} onSubmit={onSubmit} />
                )}
                {step.type === 'math' && (
                  <MathStep step={step} busy={status === 'submitting'} onSubmit={onSubmit} />
                )}
                {step.type === 'select' && (
                  <SelectStep step={step} busy={status === 'submitting'} onSubmit={onSubmit} />
                )}
              </div>
            </>
          )}

          {(status === 'loading' || status === 'idle') && (
            <div className="flex flex-col items-center gap-3 py-16 text-gray-500 dark:text-gray-400">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: ACCENT }} />
              <p className="text-sm">Loading your security check…</p>
            </div>
          )}

          {status === 'passed' && (
            <div className="flex flex-col items-center gap-3 py-16">
              <CheckCircle2 className="h-14 w-14 text-green-500" />
              <p className="text-lg font-semibold text-gray-900 dark:text-white">All done — thank you!</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Taking you to sign in…</p>
            </div>
          )}

          {status === 'failed' && (
            <div className="flex flex-col items-center gap-3 py-16">
              <ShieldCheck className="h-14 w-14 text-amber-500" />
              <p className="text-lg font-semibold text-gray-900 dark:text-white">Let&apos;s try once more</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {error || "That check didn't quite work — no stress, here's a fresh one."}
              </p>
              <button type="button" onClick={onRestart} className="pm-btn-primary mt-3 w-full">
                Try again
              </button>
            </div>
          )}

          {status === 'blocked' && (
            <BlockedPanel retryAfter={retryAfter} onRestart={onRestart} />
          )}
        </div>
      </div>
    </div>
  );
}
